import { useEffect, useState } from "react";
import { Check, Copy } from "@phosphor-icons/react";
import Nav from "@/components/windie/Nav";
import LegalFooter from "@/components/windie/LegalFooter";
import { getInstallCommand } from "@/lib/install";

const PROVIDERS = [
  ["Cua Driver", "computer use", "Click, type, and navigate a local desktop."],
  ["Blender", "3D + creative", "Model, light, and render from a prompt."],
  ["Desktop Commander", "filesystem + shell", "Read, write, and control local processes."],
  ["Basic Memory", "memory", "Keep portable knowledge in plain text."],
  ["Brightdata", "web + data", "Fetch live web data through a provider."],
];

const SIDEBAR_GROUPS = [
  {
    label: "Get started",
    items: [
      ["overview", "Overview"],
      ["install", "Install Windie"],
      ["quickstart", "Quickstart"],
    ],
  },
  {
    label: "Concepts",
    items: [
      ["harness", "The harness"],
      ["conversations", "Conversation trees"],
      ["sessions", "Sessions"],
    ],
  },
  {
    label: "Reference",
    items: [
      ["extensions", "Extensions"],
      ["api", "Local API"],
    ],
  },
];

function SectionHeading({ eyebrow, children }) {
  return (
    <div className="mb-6">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-50">
        {eyebrow}
      </p>
      <h2 className="mt-3 font-serif text-4xl leading-none tracking-tight md:text-5xl">
        {children}
      </h2>
    </div>
  );
}

function CodeBlock({ children, copyable = false, copied = false, onCopy }) {
  return (
    <div className="overflow-hidden border border-ink-10 bg-paper-2">
      {copyable ? (
        <div className="flex items-center justify-between border-b border-ink-10 px-4 py-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-50">
            terminal
          </span>
          <button
            type="button"
            onClick={onCopy}
            data-testid="docs-copy-install"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-ink-50 transition-colors hover:text-ink"
          >
            {copied ? <Check size={13} weight="bold" /> : <Copy size={13} />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      ) : null}
      <pre className="overflow-x-auto p-5 font-mono text-xs leading-relaxed text-ink md:text-sm">
        {children}
      </pre>
    </div>
  );
}

export default function Docs() {
  const [copied, setCopied] = useState(false);
  const installCommand = getInstallCommand();

  const copyCommand = async () => {
    try {
      await navigator.clipboard.writeText(installCommand);
    } catch (error) {
      // Clipboard access can be unavailable outside a secure browser context.
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <main data-testid="docs-page" className="min-h-screen bg-paper text-ink">
      <Nav />

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-x-16 px-6 pb-32 pt-28 md:px-10 md:pt-32 lg:grid-cols-[180px_minmax(0,760px)_180px]">
        <aside className="hidden lg:block">
          <div className="sticky top-28">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-50">
              Documentation
            </p>
            <nav className="mt-7 space-y-7" aria-label="Documentation navigation">
              {SIDEBAR_GROUPS.map((group) => (
                <div key={group.label}>
                  <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-ink-30">
                    {group.label}
                  </p>
                  <div className="mt-3 space-y-2 border-l border-ink-10 pl-3">
                    {group.items.map(([id, label]) => (
                      <a
                        key={id}
                        href={`#${id}`}
                        data-testid={`docs-sidebar-${id}`}
                        className="block font-mono text-[11px] leading-relaxed text-ink-50 transition-colors hover:text-ink"
                      >
                        {label}
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </nav>
          </div>
        </aside>

        <article className="min-w-0">
          <header id="overview" className="scroll-mt-28 border-b border-ink-10 pb-12">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-50">
              Windie / Docs / v0.1
            </p>
            <h1 className="mt-6 font-serif text-6xl leading-[0.92] tracking-tight md:text-8xl">
              Windie documentation
            </h1>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink-70 md:text-lg">
              Windie is a local harness for computer-native AI. Start with one
              command, then learn how conversations, sessions, extensions, and
              wakeups fit together.
            </p>
          </header>

          <section id="install" className="scroll-mt-28 border-b border-ink-10 py-16 md:py-20">
            <SectionHeading eyebrow="01 / Get started">Install Windie</SectionHeading>
            <p className="max-w-2xl text-sm leading-relaxed text-ink-70 md:text-base">
              Windie runs locally and opens in your browser. The installer sets
              up the runtime and keeps the process on your machine.
            </p>
            <div className="mt-8 max-w-2xl">
              <CodeBlock copyable copied={copied} onCopy={copyCommand}>
                {`$ ${installCommand}`}
              </CodeBlock>
            </div>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.16em] text-ink-30">
              macOS and Linux use sh · Windows uses PowerShell · 127.0.0.1:8787
            </p>
            <p className="mt-3 max-w-2xl text-xs leading-relaxed text-ink-50">
              The command follows your browser&apos;s operating system. The native installers detect x64 and ARM64 where a release asset is published.
            </p>
          </section>

          <section id="quickstart" className="scroll-mt-28 border-b border-ink-10 py-16 md:py-20">
            <SectionHeading eyebrow="02 / Get started">Quickstart</SectionHeading>
            <p className="max-w-2xl text-sm leading-relaxed text-ink-70 md:text-base">
              After installation, open the local interface and send your first
              wakeup. Chat is one entry point; the same runtime can later be
              activated by schedules, files, browsers, or system events.
            </p>
            <div className="mt-8 space-y-3">
              {[
                ["01", "Open Windie", "The local Inspector opens at 127.0.0.1:8787."],
                ["02", "Choose a model", "Connect through the OpenAI-compatible gateway."],
                ["03", "Send a wakeup", "Ask for work and inspect the path as it runs."],
              ].map(([number, title, body]) => (
                <div key={number} className="grid grid-cols-[42px_minmax(0,1fr)] gap-4 border border-ink-10 px-4 py-4 md:grid-cols-[56px_minmax(0,1fr)] md:px-5">
                  <span className="font-mono text-sm text-ink-30">{number}</span>
                  <div>
                    <h3 className="font-mono text-xs uppercase tracking-[0.12em]">{title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-ink-70">{body}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section id="harness" className="scroll-mt-28 border-b border-ink-10 py-16 md:py-20">
            <SectionHeading eyebrow="03 / Concepts">The harness</SectionHeading>
            <p className="max-w-2xl text-sm leading-relaxed text-ink-70 md:text-base">
              The harness is the layer between intent and action. It loads
              context, queries the model, applies permission policy, and records
              what happened for inspection.
            </p>
            <div className="mt-8 border border-ink-10 bg-paper-2 p-5 md:p-6">
              <div className="flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-50">
                <span className="border border-ink-10 bg-paper px-3 py-2">wakeup</span>
                <span aria-hidden>→</span>
                <span className="border border-ink-10 bg-paper px-3 py-2">context</span>
                <span aria-hidden>→</span>
                <span className="border border-ink-10 bg-paper px-3 py-2">model</span>
                <span aria-hidden>→</span>
                <span className="border border-ink-10 bg-paper px-3 py-2">policy</span>
                <span aria-hidden>→</span>
                <span className="border border-ink bg-ink px-3 py-2 text-[color:var(--w-bg)]">result</span>
              </div>
            </div>
          </section>

          <section id="conversations" className="scroll-mt-28 border-b border-ink-10 py-16 md:py-20">
            <SectionHeading eyebrow="04 / Concepts">Conversations are trees.</SectionHeading>
            <p className="max-w-2xl text-sm leading-relaxed text-ink-70 md:text-base">
              Every message is a durable node. From any message, you can branch
              into a new direction, edit the history, delete a node, truncate
              everything after it, or fork the conversation into a separate copy.
              The selected head defines the path the model sees.
            </p>
            <CodeBlock>
              {`conversation
  └─ user: inspect the project
      └─ assistant: I’ll inspect the current project.
          ├─ tool: open_file(path)
          │   └─ tool output: project opened successfully
          └─ alternate branch: inspect only the scene files`}
            </CodeBlock>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {[
                ["branch", "Start from any message."],
                ["edit", "Rewrite history without starting over."],
                ["truncate", "Remove descendants after a selected head."],
                ["fork", "Make a separate conversation from a path."],
              ].map(([label, body]) => (
                <div key={label} className="border border-ink-10 px-4 py-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-50">{label}</p>
                  <p className="mt-2 text-sm leading-relaxed text-ink-70">{body}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="sessions" className="scroll-mt-28 border-b border-ink-10 py-16 md:py-20">
            <SectionHeading eyebrow="05 / Concepts">Sessions run independently.</SectionHeading>
            <p className="max-w-2xl text-sm leading-relaxed text-ink-70 md:text-base">
              A session is an executable branch over the conversation tree. It
              owns its current head, status, approvals, queued inputs, and event
              history without copying the conversation.
            </p>
            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              {[
                ["select", "Choose a head", "The backend resolves the durable branch."],
                ["run", "Advance a session", "Queries and tool turns stay ordered."],
                ["parallel", "Run another", "Separate sessions can work at the same time."],
              ].map(([label, title, body]) => (
                <div key={label} className="border border-ink-10 px-4 py-5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-30">{label}</p>
                  <p className="mt-5 font-serif text-3xl leading-none">{title}</p>
                  <p className="mt-3 text-sm leading-relaxed text-ink-70">{body}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-ink-50">
              Queries inside one session remain serialized. Different sessions
              and conversations can run in parallel without mixing their context,
              branches, or event streams.
            </p>
          </section>

          <section id="extensions" className="scroll-mt-28 border-b border-ink-10 py-16 md:py-20">
            <SectionHeading eyebrow="06 / Capabilities">Extensions give Windie reach.</SectionHeading>
            <p className="max-w-2xl text-sm leading-relaxed text-ink-70 md:text-base">
              Windie keeps the core runtime small and extends itself through
              providers. Attach the capability your task needs without changing
              the conversation model.
            </p>
            <div className="mt-8 divide-y divide-ink-10 border-y border-ink-10">
              {PROVIDERS.map(([name, category, description]) => (
                <div key={name} className="grid gap-2 py-4 md:grid-cols-[150px_150px_minmax(0,1fr)] md:items-baseline md:gap-4">
                  <h3 className="font-serif text-2xl leading-none">{name}</h3>
                  <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-50">{category}</p>
                  <p className="text-sm leading-relaxed text-ink-70">{description}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              {[
                ["MCPs", "5", "Curated providers available today."],
                ["Plugins", "0", "A future provider category."],
                ["Skills", "0", "A future reusable capability layer."],
              ].map(([label, count, body]) => (
                <div key={label} className="border border-ink-10 bg-paper-2 px-4 py-4">
                  <div className="flex items-baseline justify-between">
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-50">{label}</p>
                    <p className="font-serif text-3xl leading-none">{count}</p>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-70">{body}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-ink-50">
              Providers are attached explicitly, and tool activity stays visible
              in the session alongside its arguments, approvals, and results.
            </p>
            <a
              href="/registry"
              data-testid="docs-registry-link"
              className="mt-8 inline-flex border border-ink px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors hover:bg-ink hover:text-[color:var(--w-bg)]"
            >
              Browse the registry
            </a>
          </section>

          <section id="api" className="scroll-mt-28 py-16 md:py-20">
            <SectionHeading eyebrow="07 / Reference">Local API</SectionHeading>
            <p className="max-w-2xl text-sm leading-relaxed text-ink-70 md:text-base">
              Inspect the local runtime or query a session from your own tools.
              Health checks are public; runtime actions require the API token
              stored in your local Windie home.
            </p>
            <div className="mt-8 max-w-2xl">
              <CodeBlock>
                {`GET http://127.0.0.1:8787/api/health

POST http://127.0.0.1:8787/api/sessions/{session_id}/query
X-Windie-Api-Token: <your local token>
Content-Type: application/json

{
  "text": "inspect the project"
}`}
              </CodeBlock>
            </div>
          </section>
        </article>

        <aside className="hidden xl:block">
          <div className="sticky top-28 border-l border-ink-10 pl-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-50">
              On this page
            </p>
            <nav className="mt-5 space-y-3" aria-label="On this page">
              {[
                ["install", "Install"],
                ["quickstart", "Quickstart"],
                ["harness", "The harness"],
                ["conversations", "Conversations"],
                ["sessions", "Sessions"],
                ["extensions", "Extensions"],
                ["api", "Local API"],
              ].map(([id, label]) => (
                <a
                  key={id}
                  href={`#${id}`}
                  data-testid={`docs-toc-${id}`}
                  className="block font-mono text-[10px] leading-relaxed text-ink-50 transition-colors hover:text-ink"
                >
                  {label}
                </a>
              ))}
            </nav>
          </div>
        </aside>
      </div>

      <LegalFooter />
    </main>
  );
}
